import { useCallback, useEffect, useState } from 'react';
import {
  adminHealth,
  adminLenders,
  adminDocuments,
  adminInquiries,
  createLender,
  updateLender,
  setVersionStatus,
  previewVersionDelete,
  confirmVersionDelete,
  type AdminDocument,
  type AdminInquiry,
  type AdminLender,
  type CorpusHealth,
  adminReviewQueue,
  adminResolveReview,
  adminContactMessages,
  adminSendContactMessage,
  type AdminContactMessage,
  type ReviewItem,
} from '../api.ts';

/**
 * The admin console.
 *
 * Its real job is the annual document refresh, done by someone who is not an engineer.
 * So the operations that matter are: see what we hold, see how old it is, and take
 * something out of service when it is wrong.
 *
 * Two things are deliberately harder than they could be. Deactivating a lender is the
 * prominent action and deleting is not, because `active` already excludes a lender from
 * every search while a delete cascades through versions, cells and citations - and the
 * inquiries already answered from them lose the references that made those answers
 * checkable. And deleting a version makes you read what you are destroying before you
 * can destroy it: the server refuses unless the count is echoed back, so a mis-click
 * cannot take an hour of ingestion with it.
 */

type Tab = 'review' | 'corpus' | 'lenders' | 'inquiries' | 'messages';

/**
 * A date this console can always print.
 *
 * This was `new Date(d).toISOString().slice(0, 10)`, and `toISOString` does not return a
 * bad string on an unparseable input - it throws RangeError. Every date rendered here
 * comes from a column ingest wrote: `document_version.effective_date` is lifted out of a
 * PDF's own cover page, and one document carrying something the parser mangles is enough
 * to take the console down, because the whole table renders inside one React subtree.
 * One bad row white-screens every good one - including, pointedly, the row you would need
 * to see in order to fix it.
 *
 * An ErrorBoundary now catches that, so it costs the boundary rather than the page. That
 * is a floor, not a fix: the admin console is where somebody goes to find bad data, and
 * refusing to render because it found some is the opposite of its job.
 *
 * An unparseable value prints as itself rather than as an em dash. The dash means "no
 * date recorded" everywhere else on this screen, and a value we hold but cannot read is
 * a different problem with a different remedy - somebody has to go and look at what is
 * actually in that column.
 */
const fmtDate = (d: string | null): string => {
  if (d === null || d === undefined || d === '') return '—';
  // String() rather than trusting the annotation: this is JSON off the wire, and the
  // whole point of the function is to be total about what it is handed.
  const raw = String(d);
  const t = Date.parse(raw);
  if (Number.isNaN(t)) return raw.slice(0, 24);
  return new Date(t).toISOString().slice(0, 10);
};

/**
 * Loan amount comes over as a string because it is a numeric column and postgres.js
 * will not lose precision on one. `Number('')` is 0 and `Number('unknown')` is NaN, and
 * the previous version rendered both - `$0` for an empty string, and the literal `$NaN`
 * in the amount column of the inquiry log.
 */
const fmtMoney = (n: string | null): string => {
  if (n === null || n === undefined || String(n).trim() === '') return '—';
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `$${Math.round(v).toLocaleString()}`;
};

function StatusPill({ status }: { status: string | null }) {
  if (!status) return <span className="pill">no version</span>;
  const tone = status === 'active' ? 'var(--green)'
    : status === 'review' ? 'var(--amber)' : 'var(--muted)';
  return (
    <span className="pill" style={{ color: tone, borderColor: tone }}>{status}</span>
  );
}

export default function Admin({ onClose }: { onClose: () => void }) {
  // Opens on the review queue, because it is the only tab with a person waiting on
  // the other end of it - a broker who was told a specialist would look at their file.
  const [tab, setTab] = useState<Tab>('review');
  const [queue, setQueue] = useState<ReviewItem[]>([]);
  const [health, setHealth] = useState<CorpusHealth | null>(null);
  const [lenders, setLenders] = useState<AdminLender[]>([]);
  const [documents, setDocuments] = useState<AdminDocument[]>([]);
  const [inquiries, setInquiries] = useState<AdminInquiry[]>([]);
  const [messages, setMessages] = useState<AdminContactMessage[] | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(true);

  const refresh = useCallback(async () => {
    setBusy(true); setError('');
    try {
      // The queue loads with the rest rather than on tab switch, because its COUNT is on
      // the tab label - an admin has to see there is something waiting without clicking.
      const [h, l, d, q] = await Promise.all([
        adminHealth(), adminLenders(), adminDocuments(), adminReviewQueue(),
      ]);
      setHealth(h); setLenders(l); setDocuments(d); setQueue(q);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the console.');
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  // Loaded with the console but apart from it: the tab label counts messages that did NOT
  // reach Henry, so a held one is visible without a click - and a failure here must not take
  // the documents and lenders down with it.
  const loadMessages = useCallback(async () => {
    try { setMessages(await adminContactMessages()); }
    catch { setError('Could not load Contact us messages.'); }
  }, []);
  useEffect(() => { void loadMessages(); }, [loadMessages]);
  const undelivered = (messages ?? []).filter((m) => m.status !== 'emailed').length;

  useEffect(() => {
    if (tab !== 'inquiries' || inquiries.length > 0) return;
    adminInquiries().then(setInquiries).catch(() => {
      setError('Could not load inquiries.');
    });
  }, [tab, inquiries.length]);

  const act = async (fn: () => Promise<unknown>): Promise<void> => {
    setError('');
    try { await fn(); await refresh(); }
    catch (e) { setError(e instanceof Error ? e.message : 'That did not work.'); }
  };

  return (
    <div style={{ maxWidth: 1040, margin: '0 auto' }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 18,
      }}>
        <div style={{ display: 'flex', gap: 8 }}>
          {(['review', 'corpus', 'lenders', 'inquiries', 'messages'] as Tab[]).map((t) => (
            <button key={t} type="button"
                    className={`btn ${tab === t ? '' : 'btn-ghost'}`}
                    onClick={() => setTab(t)}>
              {t === 'review'
                ? `Needs review${queue.length > 0 ? ` (${queue.length})` : ''}`
                : t === 'corpus' ? 'Documents' : t === 'lenders' ? 'Lenders'
                : t === 'inquiries' ? 'Inquiries'
                : `Messages${undelivered > 0 ? ` (${undelivered})` : ''}`}
            </button>
          ))}
        </div>
        <button className="btn btn-ghost" onClick={onClose}>Back to search</button>
      </div>

      {error ? (
        <div className="card" style={{ borderColor: 'var(--red)', color: 'var(--red)', marginBottom: 16 }}>
          {error}
        </div>
      ) : null}

      {health ? <Health health={health} /> : null}

      {busy && !health ? <div className="card">Loading…</div> : null}

      {tab === 'review' ? <ReviewQueue queue={queue} reload={refresh} /> : null}
      {tab === 'corpus' ? <Documents documents={documents} act={act} /> : null}
      {tab === 'lenders' ? <Lenders lenders={lenders} act={act} /> : null}
      {tab === 'inquiries' ? <Inquiries inquiries={inquiries} /> : null}
      {tab === 'messages' ? <Messages messages={messages} reload={loadMessages} /> : null}
    </div>
  );
}

/* ------------------------------------------------------------------- health */

function Health({ health }: { health: CorpusHealth }) {
  const { counts, needsAttention } = health;
  const held = needsAttention.filter((n) => n.status === 'review');
  const aging = needsAttention.filter((n) => n.status !== 'review');
  const figures: [string, number][] = [
    ['Lenders', counts.lenders], ['Matrix cells', counts.cells],
    ['Sections', counts.sections], ['Chunks', counts.chunks],
    ['Inquiries', counts.inquiries],
  ];
  return (
    <div className="card" style={{ marginBottom: 18 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 28, marginBottom: needsAttention.length ? 16 : 0 }}>
        {figures.map(([label, n]) => (
          <div key={label}>
            <div style={{ fontSize: 22, fontVariantNumeric: 'tabular-nums' }}>
              {n.toLocaleString()}
            </div>
            <div className="lbl">{label}</div>
          </div>
        ))}
      </div>
      {/* Split by what the reader can act on. Everything here used to render with a
          green "active" pill under a "Needs attention" heading, which said "fine" and
          "not fine" in the same row. A document held in review is a job; a document
          that is simply old is context. */}
      {held.length > 0 ? (
        <div style={{ borderTop: '1px solid var(--cream-line)', paddingTop: 12 }}>
          <div className="lbl" style={{ marginBottom: 8 }}>Held in review — not answering searches</div>
          {held.map((n) => (
            <div key={`${n.slug}:${n.title}`} style={{ fontSize: 13, marginBottom: 6 }}>
              <span className="pill" style={{ color: 'var(--amber)', borderColor: 'var(--amber)' }}>
                review
              </span>{' '}
              <b>{n.slug}</b> · {n.title}
            </div>
          ))}
        </div>
      ) : null}
      {aging.length > 0 ? (
        <details style={{ borderTop: '1px solid var(--cream-line)', paddingTop: 12, marginTop: held.length ? 12 : 0 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--muted)' }}>
            {aging.length} document{aging.length === 1 ? '' : 's'} older than a year
          </summary>
          <div style={{ marginTop: 8 }}>
            {aging.map((n) => (
              <div key={`${n.slug}:${n.title}`} style={{ fontSize: 13, marginBottom: 5, color: 'var(--muted)' }}>
                <b>{n.slug}</b> · {n.title} — {n.age_days} days
              </div>
            ))}
          </div>
        </details>
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------------- documents */

function Documents({
  documents, act,
}: { documents: AdminDocument[]; act: (fn: () => Promise<unknown>) => Promise<void> }) {
  const [pending, setPending] = useState<
    { versionId: string; title: string; total: number; cells: number; sections: number } | null>(null);

  return (
    <div className="card">
      <h3 style={{ margin: '0 0 4px', fontSize: 18 }}>Documents</h3>
      <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--muted)' }}>
        A version held in <b>review</b> is not used to answer searches. That is the safe
        state for a document we could not read completely.
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--muted)' }}>
              <th style={{ padding: '6px 8px' }}>Lender</th>
              <th style={{ padding: '6px 8px' }}>Document</th>
              <th style={{ padding: '6px 8px' }}>Status</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Pages</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Cells</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Sections</th>
              <th style={{ padding: '6px 8px' }}>Effective</th>
              <th style={{ padding: '6px 8px' }} />
            </tr>
          </thead>
          <tbody>
            {documents.map((d) => (
              <tr key={d.version_id ?? d.id} style={{ borderTop: '1px solid var(--cream-line)' }}>
                <td style={{ padding: '8px' }}>{d.lender_slug}</td>
                <td style={{ padding: '8px' }}>
                  {d.title}
                  <span style={{ color: 'var(--faint)' }}> · {d.kind}</span>
                </td>
                <td style={{ padding: '8px' }}><StatusPill status={d.status} /></td>
                <td style={{ padding: '8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {d.page_count ?? '—'}
                </td>
                <td style={{ padding: '8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {d.cells || '—'}
                </td>
                <td style={{ padding: '8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {d.sections || '—'}
                </td>
                <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>
                  {fmtDate(d.effective_date)}
                  {d.effective_date === null ? (
                    // A null date is invisible to the staleness check, whose test is
                    // `effective_date < now() - 365`. The one document whose age nobody
                    // knows would otherwise be the one never flagged.
                    <span style={{ color: 'var(--amber)' }}> · undated</span>
                  ) : null}
                </td>
                <td style={{ padding: '8px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                  {d.version_id ? (
                    <>
                      {d.status === 'active' ? (
                        <button className="btn btn-ghost" style={{ marginRight: 6 }}
                                onClick={() => void act(() => setVersionStatus(d.version_id!, 'review'))}>
                          Hold
                        </button>
                      ) : (
                        <button className="btn btn-ghost" style={{ marginRight: 6 }}
                                onClick={() => void act(() => setVersionStatus(d.version_id!, 'active'))}>
                          Activate
                        </button>
                      )}
                      <button className="btn btn-ghost" style={{ color: 'var(--red)' }}
                              onClick={() => {
                                void previewVersionDelete(d.version_id!)
                                  .then((p) => setPending({
                                    versionId: d.version_id!, title: p.title,
                                    total: p.willDelete.total,
                                    cells: p.willDelete.cells, sections: p.willDelete.sections,
                                  }));
                              }}>
                        Delete
                      </button>
                    </>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pending ? (
        <div onClick={() => setPending(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,.5)', zIndex: 60,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }}>
          <div className="card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 17 }}>Delete this version?</h3>
            <p style={{ fontSize: 14, lineHeight: 1.55 }}>
              <b>{pending.title}</b> — this removes <b>{pending.cells.toLocaleString()}</b> matrix
              cells and <b>{pending.sections.toLocaleString()}</b> guideline sections, along with
              every citation that pointed at them. Answers already given from this version will
              lose the references that made them checkable.
            </p>
            <p style={{ fontSize: 13, color: 'var(--muted)' }}>
              If you only want it out of service, close this and choose <b>Hold</b> instead —
              that is reversible.
            </p>
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button className="btn btn-ghost" onClick={() => setPending(null)}>Cancel</button>
              <button className="btn" style={{ background: 'var(--red)', borderColor: 'var(--red)' }}
                      onClick={() => {
                        const p = pending;
                        setPending(null);
                        void act(() => confirmVersionDelete(p.versionId, p.total));
                      }}>
                Delete {pending.total.toLocaleString()} rows
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ lenders */

function Lenders({
  lenders, act,
}: { lenders: AdminLender[]; act: (fn: () => Promise<unknown>) => Promise<void> }) {
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');

  return (
    <div className="card">
      <h3 style={{ margin: '0 0 14px', fontSize: 18 }}>Lenders</h3>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--muted)' }}>
              <th style={{ padding: '6px 8px' }}>Lender</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Programs</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Cells</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Docs</th>
              <th style={{ padding: '6px 8px' }}>Newest</th>
              <th style={{ padding: '6px 8px' }} />
            </tr>
          </thead>
          <tbody>
            {lenders.map((l) => (
              <tr key={l.id} style={{ borderTop: '1px solid var(--cream-line)' }}>
                <td style={{ padding: '8px' }}>
                  {l.name}
                  <span style={{ color: 'var(--faint)' }}> · {l.slug}</span>
                  {!l.active ? <span className="pill" style={{ marginLeft: 8 }}>inactive</span> : null}
                </td>
                <td style={{ padding: '8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{l.programs}</td>
                <td style={{ padding: '8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{l.cells.toLocaleString()}</td>
                <td style={{ padding: '8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{l.documents}</td>
                <td style={{ padding: '8px' }}>{fmtDate(l.newest_document)}</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>
                  <button className="btn btn-ghost"
                          onClick={() => void act(() => updateLender(l.id, { active: !l.active }))}>
                    {l.active ? 'Deactivate' : 'Reactivate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ borderTop: '1px solid var(--cream-line)', marginTop: 14, paddingTop: 14 }}>
        <div className="lbl" style={{ marginBottom: 8 }}>Add a lender</div>
        <form
          style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
          onSubmit={(e) => {
            e.preventDefault();
            if (!slug || !name) return;
            void act(() => createLender({ slug, name })).then(() => { setSlug(''); setName(''); });
          }}
        >
          <input className="finput" value={name} onChange={(e) => setName(e.target.value)}
                 placeholder="Name, e.g. Angel Oak Mortgage" style={{ flex: '1 1 240px' }} />
          <input className="finput" value={slug} onChange={(e) => setSlug(e.target.value)}
                 placeholder="slug, e.g. angel-oak" style={{ flex: '0 1 200px' }} />
          <button className="btn" type="submit" disabled={!slug || !name}>Add</button>
        </form>
        <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8, marginBottom: 0 }}>
          Adding a lender here creates the record. Its documents are ingested separately —
          a lender with no matrix answers nothing, and says so rather than declining.
        </p>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- inquiries */

function Inquiries({ inquiries }: { inquiries: AdminInquiry[] }) {
  return (
    <div className="card">
      <h3 style={{ margin: '0 0 4px', fontSize: 18 }}>Recent inquiries</h3>
      <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--muted)' }}>
        What brokers actually asked. The scenarios that produced few citations are the
        ones worth reading — they are where the corpus came up short.
      </p>
      {inquiries.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--muted)' }}>Nothing yet.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--muted)' }}>
                <th style={{ padding: '6px 8px' }}>When</th>
                <th style={{ padding: '6px 8px' }}>Who</th>
                <th style={{ padding: '6px 8px' }}>Scenario</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Amount</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>FICO</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Cites</th>
              </tr>
            </thead>
            <tbody>
              {inquiries.map((q) => (
                <tr key={q.id} style={{ borderTop: '1px solid var(--cream-line)' }}>
                  <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{fmtDate(q.created_at)}</td>
                  <td style={{ padding: '8px' }}>{q.username ?? '—'}</td>
                  <td style={{ padding: '8px', maxWidth: 420 }}>
                    {q.funky_text ?? <span style={{ color: 'var(--faint)' }}>no free text</span>}
                    {!q.answered ? (
                      <span className="pill" style={{ marginLeft: 8, color: 'var(--red)', borderColor: 'var(--red)' }}>
                        no answer
                      </span>
                    ) : null}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {fmtMoney(q.loan_amount)}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {q.fico ?? '—'}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {q.citations}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- messages */

const HELD_LABEL: Record<NonNullable<AdminContactMessage['held']>, string> = {
  duplicate: 'held: same words already sent',
  sender_cap: 'held: sender over 3 a day',
  hourly_cap: 'held: hourly email cap',
  daily_cap: 'held: daily email cap',
};

function messageStatus(m: AdminContactMessage): { text: string; tone: string } {
  switch (m.status) {
    case 'emailed': return { text: 'emailed to Henry', tone: 'var(--muted)' };
    case 'held': return { text: m.held ? HELD_LABEL[m.held] : 'held', tone: 'var(--red)' };
    case 'failed': return { text: `email failed${m.emailError ? `: ${m.emailError.slice(0, 60)}` : ''}`, tone: 'var(--red)' };
    case 'sending': return { text: 'sending (stuck if this stays)', tone: 'var(--red)' };
    case 'pending': return { text: 'not sent (interrupted)', tone: 'var(--red)' };
  }
}

/**
 * Contact us messages, newest first, and the one decision the flood caps cannot make.
 *
 * The caps in routes/contact.ts hold messages rather than email them once a flood has spent
 * the budget - which protects Henry's inbox and, without this screen, would also bury the one
 * real broker who wrote in during the flood. Every message that did not reach him is marked
 * here, and "Send to Henry" emails it now, caps or not: a person has read it and decided.
 */
function Messages({ messages, reload }: {
  messages: AdminContactMessage[] | null; reload: () => Promise<void>;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState('');

  const sendOne = async (id: string) => {
    setBusy(id); setNote('');
    try {
      const r = await adminSendContactMessage(id);
      setNote(r.sent ? `Message #${id} emailed to Henry.` : `Message #${id} did not send: ${r.reason ?? 'unknown'}`);
    } catch (e) {
      setNote(e instanceof Error ? `Message #${id}: ${e.message}` : `Message #${id} did not send.`);
    } finally {
      setBusy(null);
      await reload();
    }
  };

  return (
    <div className="card">
      <h3 style={{ margin: '0 0 4px', fontSize: 18 }}>Contact us messages</h3>
      <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--muted)' }}>
        Every message sent through the Contact us page. Anything marked in red did not reach
        Henry — usually held by the flood caps — and can be sent to him from here.
      </p>
      {note ? <div role="status" style={{ fontSize: 13, marginBottom: 10 }}>{note}</div> : null}
      {messages === null ? (
        <div style={{ fontSize: 13, color: 'var(--muted)' }}>Loading…</div>
      ) : messages.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--muted)' }}>Nothing yet.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--muted)' }}>
                <th style={{ padding: '6px 8px' }}>When</th>
                <th style={{ padding: '6px 8px' }}>From</th>
                <th style={{ padding: '6px 8px' }}>Message</th>
                <th style={{ padding: '6px 8px' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {messages.map((m) => {
                const st = messageStatus(m);
                return (
                  <tr key={m.id} style={{ borderTop: '1px solid var(--cream-line)', verticalAlign: 'top' }}>
                    <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{fmtDate(m.createdAt)}</td>
                    <td style={{ padding: '8px' }}>
                      {m.name}<br />
                      <span style={{ color: 'var(--muted)' }}>{m.email} · {m.phone}</span>
                    </td>
                    <td style={{ padding: '8px', maxWidth: 420, whiteSpace: 'pre-wrap' }}>{m.message}</td>
                    <td style={{ padding: '8px', minWidth: 160 }}>
                      <div style={{ color: st.tone }}>{st.text}</div>
                      {m.status !== 'emailed' ? (
                        <button type="button" className="btn btn-ghost" style={{ marginTop: 6 }}
                                disabled={busy !== null} onClick={() => { void sendOne(m.id); }}>
                          {busy === m.id ? 'Sending…' : 'Send to Henry'}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------ review queue */

/**
 * Files nobody could place, waiting on a person.
 *
 * When no lender comes back able to do a file, the answer tells the broker a specialist
 * will review it. That message shipped for a while reaching nobody - it rendered into
 * the page and nothing recorded it, so finding such a file meant reading every inquiry.
 * Telling someone a person will look, with no way for a person to know, is worse than
 * not offering.
 *
 * The note is the point of resolving, not the timestamp. "Placed with Logan at 70",
 * "nobody does this LTV on a condotel" - that is the only durable record of an answer
 * the product could not give itself, and the raw material for the next eval scenario.
 */
function ReviewQueue(
  { queue, reload }: { queue: ReviewItem[]; reload: () => Promise<void> },
): React.ReactElement {
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  if (queue.length === 0) {
    return (
      <div className="card">
        <h3 style={{ margin: '0 0 8px', fontSize: 20 }}>Nothing waiting</h3>
        <p style={{ margin: 0, fontSize: 14, color: 'var(--muted)' }}>
          Files land here only when no lender can do them and the broker has been told a
          specialist will look. An empty queue means every file so far had somewhere to go.
        </p>
      </div>
    );
  }

  const resolve = async (id: string): Promise<void> => {
    setBusy(id);
    try {
      await adminResolveReview(id, notes[id] ?? '');
      await reload();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="card">
      <h3 style={{ margin: '0 0 4px', fontSize: 20 }}>
        {queue.length} file{queue.length === 1 ? '' : 's'} nobody could place
      </h3>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--muted)' }}>
        Each of these brokers was told one of our specialists would review the file.
      </p>
      {queue.map((q) => (
        <div key={q.id} style={{
          borderTop: '1px solid var(--line)', paddingTop: 12, marginTop: 12,
        }}>
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>
            {fmtDate(q.created_at)} · {q.username ?? 'unknown'} · {q.state ?? '—'} ·
            {' '}{q.doc_type ?? '—'} · {q.purpose ?? '—'} · FICO {q.fico ?? '—'} ·
            {' '}{fmtMoney(q.loan_amount)} on {fmtMoney(q.property_value)}
          </div>
          {q.funky_text ? (
            <div className="serif" style={{ fontSize: 15, margin: '6px 0' }}>
              “{q.funky_text}”
            </div>
          ) : null}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <input className="finput" style={{ flex: 1 }}
                   placeholder="What happened to it — where it was placed, or why nobody could"
                   value={notes[q.id] ?? ''}
                   onChange={(e) => setNotes((n) => ({ ...n, [q.id]: e.target.value }))} />
            <button type="button" className="btn" disabled={busy === q.id}
                    onClick={() => void resolve(q.id)}>
              {busy === q.id ? 'Saving…' : 'Reviewed'}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
